import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import api, { enrichProperty } from '../lib/api';

const PROPERTY_TYPES = ['House','Condominium','Land','Commercial','Townhouse','Apartment'];

export default function Properties() {
  const { transactionType, type } = useParams();
  const [searchParams] = useSearchParams();

  const [q, setQ] = useState(searchParams.get('q') || '');
  const [loc, setLoc] = useState(searchParams.get('location') || 'any');
  const [propType, setPropType] = useState(type || 'any');
  const [beds, setBeds] = useState('any');
  const [priceRange, setPriceRange] = useState([0, 50000000]);
  const [sort, setSort] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  useEffect(() => { api.get('/locations').then(r => setLocations(r.data || [])); }, []);
  useEffect(() => { setPropType(type || 'any'); }, [type]);

  const titleLabel = transactionType === 'for-rent' ? 'Properties for Rent' : (transactionType === 'for-sale' ? 'Properties for Sale' : 'All Properties');

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (transactionType) params.txn = transactionType;
    if (propType !== 'any') params.type = propType;
    if (loc !== 'any') params.location = loc;
    if (beds !== 'any') params.beds = parseInt(beds);
    if (q.trim()) params.q = q.trim();
    params.min_price = priceRange[0];
    params.max_price = priceRange[1];
    params.sort = sort;
    params.limit = 60;
    const t = setTimeout(() => {
      api.get('/properties', { params })
        .then(r => { setItems((r.data?.items || []).map(enrichProperty)); setTotal(r.data?.total || 0); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [transactionType, propType, loc, beds, q, priceRange, sort]);

  const reset = () => { setQ(''); setLoc('any'); setPropType('any'); setBeds('any'); setPriceRange([0, 50000000]); };

  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="text-3xl md:text-4xl font-bold">{titleLabel}</h1>
          <p className="text-neutral-300 mt-2">{total} properties available across the Philippines</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className={'lg:col-span-1 ' + (showFilters ? 'block' : 'hidden lg:block')}>
          <div className="bg-white rounded-xl p-5 border border-neutral-200 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-neutral-900">Filters</h3>
              <button onClick={reset} className="text-xs text-[#DC2626] font-medium">Reset</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-neutral-700 mb-1 block">Keyword</label>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, city, or address" />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-700 mb-1 block">Location</label>
                <Select value={loc} onValueChange={setLoc}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">All Locations</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-700 mb-1 block">Property Type</label>
                <Select value={propType} onValueChange={setPropType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Type</SelectItem>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-700 mb-1 block">Bedrooms (min)</label>
                <Select value={beds} onValueChange={setBeds}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n}+</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-700 mb-2 block">Price Range</label>
                <Slider value={priceRange} onValueChange={setPriceRange} min={0} max={50000000} step={500000} />
                <div className="flex justify-between text-xs text-neutral-600 mt-2">
                  <span>₱{priceRange[0].toLocaleString()}</span>
                  <span>₱{priceRange[1].toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-5">
            <Button variant="outline" className="lg:hidden gap-2" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />} Filters
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-neutral-600">Sort by:</span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-neutral-500">Loading properties...</div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-neutral-200">
              <p className="text-neutral-600">No properties match your filters.</p>
              <Button onClick={reset} className="btn-primary mt-4">Reset Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
